import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import * as pdfjs from "pdfjs-dist";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const MIN_SCALE = 0.6;
const MAX_SCALE = 2.4;
const SCALE_STEP = 0.2;

export default function PdfViewerModal({ pdfUrl, title, onClose }) {
  const viewerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(760);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1);
  const [fitWidth, setFitWidth] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const pdfFile = useMemo(() => (pdfUrl ? { url: pdfUrl } : null), [pdfUrl]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowLeft") setPageNumber((page) => Math.max(1, page - 1));
      if (event.key === "ArrowRight") {
        setPageNumber((page) => (numPages ? Math.min(numPages, page + 1) : page));
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [numPages, onClose]);

  useEffect(() => {
    const node = viewerRef.current;
    if (!node) return undefined;

    const updateWidth = () => {
      setContainerWidth(Math.max(280, Math.floor(node.clientWidth - 32)));
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  const onDocumentLoadSuccess = useCallback(({ numPages: loadedPages }) => {
    setNumPages(loadedPages);
    setPageNumber(1);
    setLoadError(false);
  }, []);

  const changeScale = (nextScale) => {
    setFitWidth(false);
    setScale(Math.min(MAX_SCALE, Math.max(MIN_SCALE, nextScale)));
  };

  const pageWidth = fitWidth ? containerWidth : Math.round(containerWidth * scale);

  return (
    <div className="pdf-modal-backdrop" role="dialog" aria-modal="true" aria-label={`${title} PDF viewer`}>
      <div className="pdf-modal">
        <header className="pdf-modal-header">
          <h2>{title}</h2>
          <button className="pdf-icon-button" type="button" onClick={onClose} aria-label="Close PDF viewer">
            x
          </button>
        </header>

        <div className="pdf-toolbar">
          <button type="button" onClick={() => setPageNumber((page) => Math.max(1, page - 1))} disabled={pageNumber <= 1 || loadError}>
            Previous Page
          </button>
          <span className="pdf-page-count">
            {loadError ? "Preview unavailable" : `Page ${pageNumber} / ${numPages || "-"}`}
          </span>
          <button type="button" onClick={() => setPageNumber((page) => Math.min(numPages || page, page + 1))} disabled={!numPages || pageNumber >= numPages || loadError}>
            Next Page
          </button>
          <button type="button" onClick={() => changeScale(scale - SCALE_STEP)} disabled={fitWidth ? false : scale <= MIN_SCALE || loadError}>
            Zoom Out
          </button>
          <button type="button" onClick={() => changeScale(scale + SCALE_STEP)} disabled={fitWidth ? false : scale >= MAX_SCALE || loadError}>
            Zoom In
          </button>
          <button type="button" onClick={() => setFitWidth(true)} disabled={loadError}>
            Fit Width
          </button>
          <a className="pdf-download-button" href={pdfUrl} download target="_blank" rel="noopener noreferrer">
            Download PDF
          </a>
        </div>

        <main className="pdf-modal-viewer" ref={viewerRef}>
          {loadError ? (
            <div className="pdf-modal-state">Preview unavailable</div>
          ) : (
            <Document
              file={pdfFile}
              loading={<div className="pdf-modal-state"><span className="pdf-spinner" />Loading PDF...</div>}
              error={<div className="pdf-modal-state">Preview unavailable</div>}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={(error) => {
                console.warn("PDF viewer failed to load", error);
                setLoadError(true);
              }}
            >
              <Page
                pageNumber={pageNumber}
                width={pageWidth}
                loading={<div className="pdf-modal-state"><span className="pdf-spinner" />Loading page...</div>}
              />
            </Document>
          )}
        </main>
      </div>
    </div>
  );
}
